import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useParams, useNavigate, Link, useLocation, Location} from 'react-router-dom'
import {useLazyQuery} from '@apollo/client'
import {GET_ORDER} from 'app/features/orders/graphql'
import {Button, ErrorMessage, SimplePage} from '_core'
import {formatToShortDateString} from 'app/format/date'
import {Customer} from 'app/features/orders/components'
import {
  setOrder,
  resetFulfillmentState,
  selectFulfillmentViewOrder,
  selectFulfillmentOrderItems,
  selectFulfillmentTracking,
} from 'app/features/orders/redux/fulfillment/orderSlices'
import fulfillmentService from 'app/features/orders/services/FulfillmentService'
import bulkFulfillmentService from 'app/services/BulkFulfillmentService'
import {ShippingItems} from 'app/features/orders/fulfillments/components'
import TrackingInformation from './components/TrackingInformation'
import {showMessage} from 'app/redux/messageSlice'
import {RootState} from 'setup'
import {deliveryTypes, fulfillmentStatusTypes} from 'app/constant'
import filter from 'lodash/filter'
import {OrderHeader} from '../../componentsv1'
import {addressTypes, mapToAddress} from '../../redux/order/helper'

type Props = {}

const ManualShipping: FC<Props> = () => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [getOrder, {data, loading, error}] = useLazyQuery(GET_ORDER, {
    fetchPolicy: 'cache-and-network',
  })
  const order = useSelector<RootState, any>(selectFulfillmentViewOrder)
  const orderItems = useSelector<RootState, any[]>(selectFulfillmentOrderItems)
  const tracking = useSelector<RootState, any>(selectFulfillmentTracking)
  const history = useNavigate()
  const {orderId} = useParams<any>()
  const {state}: Location = useLocation()

  useEffect(() => {
    if (orderId) {
      getOrder({variables: {id: orderId}})
    }
  }, [orderId, getOrder])

  useEffect(() => {
    if (data) {
      dispatch(
        setOrder({
          ...data.order,
          shippingAddress: mapToAddress(
            data.order.orderAddressProfiles?.nodes,
            data.order.shippingAddress,
            addressTypes.shipping
          ),
          billingAddress: mapToAddress(
            data.order.orderAddressProfiles?.nodes,
            data.order.billingAddress,
            addressTypes.billing
          ),
        })
      )
    }
  }, [dispatch, data])

  const handleFulfillment = async () => {
    try {
      setIsSubmitting(true)
      if (order.fulfillmentStatus === fulfillmentStatusTypes.readyToFulfill) {
        await bulkFulfillmentService.updateInHouseFulfillment({orderId: order.id, tracking})
      } else {
        await fulfillmentService.createManualFulfillment(order, tracking)
        dispatch(resetFulfillmentState())
      }
      dispatch(showMessage({text: 'Order fulfilled successfully', variant: 'success'}))
      history(`/orders/${order.id}/fulfillments/view`, {state: redirect.locationState})
    } catch (error: any) {
      dispatch(showMessage({text: error.message, variant: 'error'}))
      setIsSubmitting(false)
    }
  }

  const isValid = useMemo(() => {
    if (fulfillmentStatusTypes.readyToFulfill === order?.fulfillmentStatus) {
      return true
    }
    const filterItems = filter(orderItems, ({fulfillmentQuantity}) => fulfillmentQuantity > 0)
    return filterItems.length > 0
  }, [order?.fulfillmentStatus, orderItems])

  const redirect = useMemo(() => {
    if (state?.redirectURL) {
      return {path: state?.redirectURL, locationState: {prevRedirectURL: state?.redirectURL}}
    }
    return {path: `/orders/${orderId}/fulfillments/view`, locationState: null}
  }, [orderId, state])

  return (
    <SimplePage loading={loading} error={error}>
      {order && (
        <>
          <OrderHeader
            title={<>Fulfillment - {`#${order.orderNumber}`}</>}
            routePath={redirect.path}
            locationState={redirect.locationState}
            subTitle={
              <div className='d-flex flex-wrap flex-stack'>
                <div className='d-flex flex-column'>
                  <div className='text-muted fs-7 fw-bold'>
                    {formatToShortDateString(order.createdAt)}
                  </div>
                </div>
              </div>
            }
          />
          <div className='row gy-5 g-xl-8'>
            <div className='col-xl-8'>
              <ShippingItems items={orderItems} fulfillmentStatus={order.fulfillmentStatus} />

              {order?.orderDeliveryMethod === deliveryTypes.ship && (
                <TrackingInformation data={tracking} />
              )}
              {!isValid && (
                <ErrorMessage error='Please update the item minimum 1 quantity for fulfillment' />
              )}
              <div className='row gy-5 gx-xl-8 mb-5 float-end'>
                <div className='col-auto'>
                  <Link
                    id='btnManualCancel'
                    type='button'
                    to={redirect.path}
                    className='btn explore-btn-outline'
                    state={redirect.locationState}
                  >
                    Cancel
                  </Link>
                </div>
                <div className='col-auto'>
                  <Button
                    id='btnManualSubmit'
                    type='button'
                    title='Fulfill Item'
                    onClick={handleFulfillment}
                    className='btn btn-primary'
                    isSubmitting={isSubmitting}
                    isValid={true}
                    disabled={!isValid}
                  />
                </div>
              </div>
            </div>
            <div className='col-xl-4'>
              <Customer
                id={order?.id}
                customer={order.customer}
                shippingAddress={order?.shippingAddress}
                billingAddress={order?.billingAddress}
                paymentMethod={order?.paymentMethod}
                isShowCompany={order?.orderDeliveryMethod === deliveryTypes.ship}
              />
            </div>
          </div>
        </>
      )}
    </SimplePage>
  )
}

export default ManualShipping
